import './style.scss'
import _ from 'underscore'
import Backbone from '@polleverywhere/backbone'
import ArchivedView from './archived'
import ClosedView from './closed'
import FullView from './full'
import InactiveView from './inactive'
import OpenView from './open'
import { calculateTextScale, measureText, normalizeScale } from '@polleverywhere/viz-utils'
import '@polleverywhere/icon-element'

const MODEL_CHANGE_EVENTS = [
  'activated',
  'state',
  'maxed_out',
  'simple_keywords',
  'shortcode',
  'keyword',
  'session_keyword_format_description'
]

const STATE_MAP = {
  archived: ArchivedView,
  closed: ClosedView,
  full: FullView,
  inactive: InactiveView,
  open: OpenView
}

function getViewState () {
  // Holds the conditions when presenters are required to be logged in.
  // NOTE: Currently, login is only required when inside presenter apps, but
  // additional scenarios may come up in the future.
  const requireLogin = this.isPresenter()

  // If login is required and presenter is not logged in, always display the
  // warning message instructing them to login.
  const userIsRequiredAndNotLoggedIn = requireLogin && !this.user.isLoggedIn()

  const archived = userIsRequiredAndNotLoggedIn ? false : this.showingArchive()
  const closed = userIsRequiredAndNotLoggedIn ? false : (!archived && this.model.get('state') === 'closed')
  const maxedOut = userIsRequiredAndNotLoggedIn ? false : (this.model.get('state') === 'maxed_out')
  const open = userIsRequiredAndNotLoggedIn ? false : (!archived && !closed && !maxedOut)

  if (archived) { return 'archived' }
  if (closed) { return 'closed' }
  if (maxedOut) { return 'full' }
  if (open) { return 'open' }
  return 'inactive'
}

export class VotingInstructions extends Backbone.View {
  get className () { return 'voting_instructions' }

  initialize ({ user, settings, i18n, forceVotingInstructions }) {
    if (!i18n) {
      console.error('i18n is required.')
    }
    this.i18n = i18n
    this.forceVotingInstructions = forceVotingInstructions
    this.customizeScale = this.customizeScale.bind(this)
    this.customizePlacement = this.customizePlacement.bind(this)
    this.customizeBackgroundColor = this.customizeBackgroundColor.bind(this)
    this.numberOfModalities = this.numberOfModalities.bind(this)
    this.isPresenter = this.isPresenter.bind(this)
    this.updateInstructions = this.updateInstructions.bind(this)
    this.toggleVotingInstructions = this.toggleVotingInstructions.bind(this)
    this.showingArchive = this.showingArchive.bind(this)
    this.renderAnonymous = this.renderAnonymous.bind(this)
    this.applyAnonymousColors = this.applyAnonymousColors.bind(this)
    this.render = this.render.bind(this)

    this.user = user
    this.settings = settings
    this.flashOptions = this.model.flashOptions()

    const throttledUpdateInstructions = _.throttle(this.updateInstructions, 10, { leading: false })

    this.listenTo(this.user, 'change:id', throttledUpdateInstructions)

    this.listenTo(this.model, `change:${MODEL_CHANGE_EVENTS.join(' change:')}`, throttledUpdateInstructions)
    this.listenTo(this.model.presenter, 'change', throttledUpdateInstructions)
    this.listenTo(this.model, 'change:anonymous', this.renderAnonymous)

    this.listenTo(this.flashOptions, 'change:voting_instructions_text_color', this.customizeTextColor)
    this.listenTo(this.flashOptions, 'change:voting_instructions_background_color', this.customizeBackgroundColor)
    this.listenTo(this.flashOptions, 'change:voting_instructions_scale', this.customizeScale)
    this.listenTo(this.flashOptions, 'change:showInstructions', this.toggleVotingInstructions)
    this.listenTo(this.flashOptions, 'change:teamPollArchiveId', throttledUpdateInstructions)
    this.listenTo(this.flashOptions, 'change:instructionPlacement', this.render)
    this.listenTo(this.flashOptions, 'change:themeId', this.applyAnonymousColors)
    this.listenTo(this.flashOptions, 'discardChanges', this.applyAnonymousColors)
  }

  customizeTextColor () {
    const color = this.flashOptions.get('voting_instructions_text_color')
    this.$el.css({ color })
  }

  customizeBackgroundColor () {
    const backgroundColor = this.flashOptions.get('voting_instructions_background_color')
    if (backgroundColor) {
      this.$el.css({ backgroundColor })
    }
  }

  customizeScale () {
    let vizSettingsScale = this.flashOptions.get('voting_instructions_scale')
    if (vizSettingsScale === null || vizSettingsScale === undefined) {
      vizSettingsScale = 0.75
    }
    const targetSize = normalizeScale(vizSettingsScale, 0, 1, 2, 3)
    const targetSizeString = `${targetSize}em`
    this.$el.css('fontSize', targetSizeString)

    const font = this.flashOptions.get('font')
    const smsText = measureText(this.$el.find('.basic-sms').text(), font, targetSizeString)
    const webText = measureText(this.$el.find('.modality.web').text(), font, targetSizeString)

    if (webText.width > window.innerWidth || smsText.width > window.innerWidth) {
      const longerText = webText.width > smsText.width ? webText : smsText
      this.adjustScaleToOneLine(longerText)
    }
    this.trigger('resize')
  }

  adjustScaleToOneLine (textSize) {
    const adjustedScale = this.calculateVotingInstructionsTextSize(textSize)
    const scaledToOneLine = normalizeScale(adjustedScale, 0, 1, 2, 3)
    this.$el.css('fontSize', `${scaledToOneLine}em`)
  }

  calculateVotingInstructionsTextSize (textSize) {
    const boxSize = {
      height: window.innerHeight * 0.13, // instructions are 13vh
      width: window.innerWidth
    }

    return calculateTextScale(boxSize, textSize, undefined, false)
  }

  customizePlacement () {
    this.$el.removeClass('left right center')
    const placement = this.flashOptions.get('instructionPlacement') || 'center'
    this.$el.addClass(placement)
  }

  numberOfModalities () {
    const smsEnabled = this.model.get('sms_enabled')
    const webEnabled = this.model.get('web_enabled')
    const active = this.model.isActivated()

    return _.compact([smsEnabled, webEnabled && active]).length
  }

  // Override if voting_instructions is passed via url param
  isPresenter () {
    return this.settings && this.settings.get('push') === 'voting_instructions'
  }

  updateInstructions () {
    const state = getViewState.call(this)

    this.view && this.view.remove() && delete this.view

    this.view = new STATE_MAP[state]({
      i18n: this.i18n,
      settings: this.settings,
      model: this.model,
      user: this.user
    })

    this.$el.html(this.view.render().$el)
    this.trigger('resize')

    this.listenTo(this.view, 'login', this.trigger.bind(this, 'login'))
    this.listenTo(this.view, 'qrcode', this.trigger.bind(this, 'qrcode'))
  }

  toggleVotingInstructions () {
    let showInstructions = this.forceVotingInstructions
    if (showInstructions === undefined) {
      showInstructions = this.flashOptions.get('showInstructions') !== false
    }

    if (showInstructions) { this.updateInstructions() }
    this.$el.toggleClass('hidden', !showInstructions)
  }

  showingArchive () {
    return typeof this.model.flashOptions === 'function' &&
      this.flashOptions &&
      this.flashOptions.has('teamPollArchiveId') &&
      this.flashOptions.get('teamPollArchiveId') !== 0
  }

  renderAnonymous () {
    if (!this.model.get('anonymous')) return

    this.applyAnonymousColors()

    const anonymousContainer = document.createElement('div')
    this.el.append(anonymousContainer)
    anonymousContainer.className = 'anonymous-icon-container'

    const anonymousIcon = document.createElement('icon-element')
    anonymousIcon.setAttribute('name', 'anonymous-round')
    anonymousIcon.setAttribute('type', 'svg')

    anonymousContainer.append(anonymousIcon)
  }

  applyAnonymousColors () {
    // anonymous polls with classic theme should have dark voting instructions with a white font
    if (!this.model.get('anonymous') || this.flashOptions.get('themeId') !== 'classic') return

    this.flashOptions.set({ voting_instructions_background_color: '#1e293b', voting_instructions_text_color: 'white' })
  }

  render () {
    this.$el.toggleClass('singleMode', this.numberOfModalities() === 1)
    this.$el.addClass(this.model.type())

    // show appropriate sections
    this.toggleVotingInstructions()

    // apply user customizations
    this.customizeTextColor()
    this.customizeScale()
    this.customizePlacement()
    this.customizeBackgroundColor()
    this.renderAnonymous()

    return this
  }
}
